(() => {
  const templateSelector = 'template[data-product-card-video-template]';
  const initialized = new WeakSet();
  const controllers = new WeakMap();

  const pauseExternalVideo = (iframe) => {
    if (iframe.dataset.host === 'youtube') {
      iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    } else if (iframe.dataset.host === 'vimeo') {
      iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
    }
  };

  const playExternalVideo = (iframe) => {
    if (iframe.dataset.host === 'youtube') {
      iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    } else if (iframe.dataset.host === 'vimeo') {
      iframe.contentWindow?.postMessage('{"method":"play"}', '*');
    }
  };

  const hidePoster = (poster) => {
    if (!poster) return;
    poster.hidden = true;
    poster.style.display = 'none';
  };

  const createController = (hoverImages) => {
    const slider = hoverImages.querySelector('[data-hover-slider]');
    const slides = Array.from(hoverImages.querySelectorAll('[data-hover-slide-touch]'));
    if (!slider || !slides.some((slide) => slide.querySelector(templateSelector))) return null;

    const state = {activeIndex: 0, frameRequested: false, visible: false};

    const activeIndex = () => {
      if (!slider.clientWidth) return 0;
      return Math.max(0, Math.min(slides.length - 1, Math.round(slider.scrollLeft / slider.clientWidth)));
    };

    const pauseSlide = (slide) => {
      slide.querySelector('video')?.pause();
      const iframe = slide.querySelector('iframe[data-host]');
      if (iframe) pauseExternalVideo(iframe);
    };

    const playVideo = (video, poster) => {
      if (!state.visible || slides[state.activeIndex] !== video.closest('[data-hover-slide-touch]')) return;
      if (!video.paused) return;

      video.addEventListener('playing', () => hidePoster(poster), {once: true});

      const playPromise = video.play();
      if (playPromise) playPromise.catch(() => {});
    };

    const loadActiveSlide = () => {
      state.activeIndex = activeIndex();

      slides.forEach((slide, index) => {
        if (!state.visible || index !== state.activeIndex) {
          pauseSlide(slide);
          return;
        }

        const template = slide.querySelector(templateSelector);
        const poster = slide.querySelector('[data-product-card-video-poster]');
        let media = slide.querySelector('video, iframe[data-host]');

        if (!media && template) {
          media = template.content.firstElementChild?.cloneNode(true);
          if (!media) return;

          template.before(media);

          if (media.nodeName === 'VIDEO') {
            media.autoplay = false;
            media.removeAttribute('autoplay');
            media.controls = false;
            media.muted = true;
            media.playsInline = true;
            media.preload = 'auto';
            media.load();
          } else {
            media.addEventListener('load', () => hidePoster(poster), {once: true});
          }
        }

        if (!media) return;

        if (media.nodeName === 'VIDEO') {
          if (media.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            playVideo(media, poster);
          } else if (!media.dataset.waitingForCardFrame) {
            media.dataset.waitingForCardFrame = 'true';
            media.addEventListener('loadeddata', () => {
              delete media.dataset.waitingForCardFrame;
              playVideo(media, poster);
            }, {once: true});
          }
        } else {
          playExternalVideo(media);
        }
      });
    };

    const scheduleUpdate = () => {
      if (state.frameRequested) return;
      state.frameRequested = true;
      requestAnimationFrame(() => {
        state.frameRequested = false;
        loadActiveSlide();
      });
    };

    slider.addEventListener('scroll', scheduleUpdate, {passive: true});

    return {
      setVisible(visible) {
        state.visible = visible;
        if (visible) {
          scheduleUpdate();
        } else {
          slides.forEach(pauseSlide);
        }
      },
    };
  };

  const viewportObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      controllers.get(entry.target)?.setVisible(entry.isIntersecting && entry.intersectionRatio > 0.15);
    });
  }, {threshold: [0, 0.15, 0.6]});

  const initialize = (root = document) => {
    const candidates = [];
    if (root instanceof Element && root.matches('hover-images')) candidates.push(root);
    root.querySelectorAll?.('hover-images').forEach((element) => candidates.push(element));

    candidates.forEach((hoverImages) => {
      if (initialized.has(hoverImages)) return;
      initialized.add(hoverImages);

      const controller = createController(hoverImages);
      if (!controller) return;

      controllers.set(hoverImages, controller);
      viewportObserver.observe(hoverImages);
    });
  };

  const start = () => {
    const isTouch = document.documentElement.classList.contains('supports-touch') || window.matchMedia('(pointer: coarse)').matches;
    if (!isTouch) return;

    initialize();

    const contentObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) initialize(node);
        });
      });
    });

    contentObserver.observe(document.querySelector('#MainContent') || document.body, {childList: true, subtree: true});
  };

  customElements.whenDefined('hover-images').then(start);
})();
